import { apiRequest } from '../api/http'
import type {
  InventoryConnectStartResponse,
  InventoryConnection,
  InventoryConnectionCreateRequest,
  InventoryConnectionSummary,
  InventoryProductPullRequest,
  InventoryProductPullResult,
  InventoryProductPullRun,
  InventoryProductPullRunIssueQuery,
  InventoryProductPullRunIssuesPage,
  InventoryProductPullRunStartResponse,
  InventoryProviderOption,
  InventorySingleItemImportRequest,
  InventorySingleItemImportResponse,
  ZohoMasterDataCategory,
  ZohoMasterDataItem,
  ZohoMasterDataRefreshRequest,
  ZohoMasterDataRefreshResult,
  ZohoOAuthCallbackRequest,
  ZohoOrderAuditQuery,
  ZohoOrderAuditResponse,
  ZohoOrderPolicy,
  ZohoOrderPolicyAutoSendRequest,
  ZohoOrderPolicySaveRequest,
} from './types'

const INVENTORY_BASE = '/api/inventory-connections'

export function getZohoOrderRoutingCapabilities(accessToken: string, id: number) {
  return apiRequest<{ uiEnabled: boolean; shadowEnabled: boolean }>(INVENTORY_BASE, `/${id}/zoho/order-routing/capabilities`, { accessToken })
}

function inventoryAuth(accessToken: string) {
  return { accessToken }
}

export function listInventoryProviders(accessToken: string): Promise<InventoryProviderOption[]> {
  return apiRequest<InventoryProviderOption[]>(INVENTORY_BASE, '/providers', inventoryAuth(accessToken))
}

export function listInventoryConnections(accessToken: string): Promise<InventoryConnection[]> {
  return apiRequest<InventoryConnection[]>(INVENTORY_BASE, '', inventoryAuth(accessToken))
}

export function createInventoryConnection(accessToken: string, payload: InventoryConnectionCreateRequest): Promise<InventoryConnection> {
  return apiRequest<InventoryConnection>(INVENTORY_BASE, '', { method: 'POST', body: payload, accessToken })
}

export function startInventoryConnect(accessToken: string, id: number): Promise<InventoryConnectStartResponse> {
  return apiRequest<InventoryConnectStartResponse>(INVENTORY_BASE, `/${id}/connect`, { method: 'POST', accessToken })
}

export function disconnectInventoryConnection(accessToken: string, id: number): Promise<void> {
  return apiRequest<void>(INVENTORY_BASE, `/${id}`, { method: 'DELETE', accessToken })
}

export function completeZohoOAuthCallback(accessToken: string, payload: ZohoOAuthCallbackRequest): Promise<InventoryConnection> {
  return apiRequest<InventoryConnection>(INVENTORY_BASE, '/zoho/oauth/callback', { method: 'POST', body: payload, accessToken })
}

export function pullInventoryProducts(accessToken: string, id: number, payload: InventoryProductPullRequest): Promise<InventoryProductPullResult> {
  return apiRequest<InventoryProductPullResult>(INVENTORY_BASE, `/${id}/items/pull`, { method: 'POST', body: payload, accessToken })
}

export function importSingleInventoryItem(
  accessToken: string,
  id: number,
  payload: InventorySingleItemImportRequest,
): Promise<InventorySingleItemImportResponse> {
  return apiRequest<InventorySingleItemImportResponse>(INVENTORY_BASE, `/${id}/items/import`, { method: 'POST', body: payload, accessToken })
}

export function startInventoryProductPullRun(accessToken: string, id: number, payload: InventoryProductPullRequest): Promise<InventoryProductPullRunStartResponse> {
  return apiRequest<InventoryProductPullRunStartResponse>(INVENTORY_BASE, `/${id}/items/pull/runs`, { method: 'POST', body: payload, accessToken })
}

export function getLatestInventoryProductPullRun(accessToken: string, id: number): Promise<InventoryProductPullRun | null> {
  return apiRequest<InventoryProductPullRun | null>(INVENTORY_BASE, `/${id}/items/pull/runs/latest`, { accessToken })
}

export function getInventoryProductPullRun(accessToken: string, id: number, runId: number): Promise<InventoryProductPullRun> {
  return apiRequest<InventoryProductPullRun>(INVENTORY_BASE, `/${id}/items/pull/runs/${runId}`, { accessToken })
}

export function getInventoryProductPullRunIssues(
  accessToken: string,
  id: number,
  runId: number,
  query: InventoryProductPullRunIssueQuery = {},
): Promise<InventoryProductPullRunIssuesPage> {
  const params = new URLSearchParams()
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.size !== undefined) params.set('size', String(query.size))
  if (query.code?.trim()) params.set('code', query.code.trim())
  if (query.query?.trim()) params.set('query', query.query.trim())
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return apiRequest<InventoryProductPullRunIssuesPage>(INVENTORY_BASE, `/${id}/items/pull/runs/${runId}/issues${suffix}`, { accessToken })
}

export function getInventoryConnectionSummary(accessToken: string, id: number): Promise<InventoryConnectionSummary> {
  return apiRequest<InventoryConnectionSummary>(INVENTORY_BASE, `/${id}/summary`, { accessToken })
}

export function listZohoMasterData(accessToken: string, id: number, category: ZohoMasterDataCategory): Promise<ZohoMasterDataItem[]> {
  const params = new URLSearchParams({ category })
  return apiRequest<ZohoMasterDataItem[]>(INVENTORY_BASE, `/${id}/zoho/master-data?${params.toString()}`, { accessToken })
}

export function refreshZohoMasterData(accessToken: string, id: number, payload: ZohoMasterDataRefreshRequest): Promise<ZohoMasterDataRefreshResult> {
  return apiRequest<ZohoMasterDataRefreshResult>(INVENTORY_BASE, `/${id}/zoho/master-data/refresh`, { method: 'POST', body: payload, accessToken })
}

export function listZohoOrderPolicies(accessToken: string, id: number): Promise<ZohoOrderPolicy[]> {
  return apiRequest<ZohoOrderPolicy[]>(INVENTORY_BASE, `/${id}/zoho/order-policies`, { accessToken })
}

export function getZohoOrderRoutingObservedValues(accessToken: string, id: number, channelConnectionIds: number[], fields: string[], limit = 50) {
  const params = new URLSearchParams({
    channelConnectionIds: channelConnectionIds.join(','),
    fields: fields.join(','),
    limit: String(limit),
  })
  return apiRequest<{ fields: Array<{ field: string; values: Array<{ value: string; orderCount: number; latestObservationTime: string; channelConnectionIds: number[]; providers: string[] }> }> }>(INVENTORY_BASE, `/${id}/zoho/order-routing/observed-values?${params}`, { accessToken })
}

export function getZohoOrderAudit(accessToken: string, id: number, query: ZohoOrderAuditQuery = {}): Promise<ZohoOrderAuditResponse> {
  const params = new URLSearchParams()
  if (query.days !== undefined) params.set('days', String(query.days))
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return apiRequest<ZohoOrderAuditResponse>(INVENTORY_BASE, `/${id}/zoho/order-audit${suffix}`, { accessToken })
}

export function getActiveZohoOrderPolicy(accessToken: string, id: number): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/active`, { accessToken })
}

export function createZohoOrderPolicy(accessToken: string, id: number, payload: ZohoOrderPolicySaveRequest): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies`, { method: 'POST', body: payload, accessToken })
}

export function updateZohoOrderPolicy(accessToken: string, id: number, policyId: number, payload: ZohoOrderPolicySaveRequest): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}`, { method: 'PUT', body: payload, accessToken })
}

export function activateZohoOrderPolicy(accessToken: string, id: number, policyId: number): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}/activate`, { method: 'POST', accessToken })
}

export function enableZohoOrderPolicy(accessToken: string, id: number, policyId: number): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}/enable`, { method: 'POST', accessToken })
}

export function setZohoOrderPolicyAutoSend(accessToken: string, id: number, policyId: number, payload: ZohoOrderPolicyAutoSendRequest): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}/auto-send`, { method: 'PUT', body: payload, accessToken })
}

export function disableZohoOrderPolicy(accessToken: string, id: number, policyId: number): Promise<ZohoOrderPolicy> {
  return apiRequest<ZohoOrderPolicy>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}/disable`, { method: 'POST', accessToken })
}

export function archiveZohoOrderPolicy(accessToken: string, id: number, policyId: number): Promise<void> {
  return apiRequest<void>(INVENTORY_BASE, `/${id}/zoho/order-policies/${policyId}`, { method: 'DELETE', accessToken })
}
